import { useState } from "react";
import { useReveal } from "./useReveal";

const WEB3FORMS_KEY = "2cf7bc79-d7ac-4faa-b826-9658ab8bf837";

type FormState = {
  name: string;
  email: string;
  message: string;
};

const initialForm: FormState = {
  name: "",
  email: "",
  message: "",
};

export function Engage() {
  const { ref, shown } = useReveal<HTMLDivElement>();
  const [form, setForm] = useState<FormState>(initialForm);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [result, setResult] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "message" ? value.slice(0, 600) : value,
    }));

    if (status !== "idle") {
      setStatus("idle");
      setResult("");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.message.trim() || status === "sending") {
      return;
    }

    setStatus("sending");
    setResult("Sending...");

    const formData = new FormData();
    formData.append("access_key", WEB3FORMS_KEY);
    formData.append("name", form.name.trim());
    formData.append("email", form.email.trim());
    formData.append("message", form.message.trim());
    formData.append("subject", "New Omnific Contact Submission");
    formData.append("from_name", "Omnific Site");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus("sent");
        setResult("Transmission received. Stand by for contact.");
        setForm(initialForm);
      } else {
        setStatus("error");
        setResult(data.message || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setResult("Something went wrong. Please try again.");
    }
  };

  const buttonLabel = status === "sending" ? "Sending..." : "Send";

  return (
    <section id="engage" className="relative hairline-t pb-28 pt-20 md:pb-36 md:pt-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 50% 50%, oklch(0.7 0.14 70 / 0.09), transparent 70%)",
        }}
      />

      <div
        ref={ref}
        className={`reveal relative mx-auto max-w-[1100px] px-6 text-center md:px-12 ${shown ? "in" : ""}`}
      >
        <div className="font-mono-tech mb-8 text-[11px] tracking-[0.32em] text-[var(--amber)]">
          ▸ THE PITCH
        </div>

        <h2 className="font-display text-[clamp(56px,10vw,160px)] leading-[0.95] tracking-[0.01em] text-bone">
          COGNIZANCE
          <br />
          <span className="text-gradient-amber italic not-italic">ALIGNMENT</span>
        </h2>

        <p className="font-body mx-auto mt-10 max-w-[760px] text-[16px] font-light leading-[1.7] text-[var(--dim-2)]">
          Why walk a 45-day path when we can fold the distance? By merging our
          accolades, we space-jump the traditional cycle and go from concept to live in
          12 hours. It's a specialized dance for the high-frequency founder. Tell me
          what you're building.
        </p>

        <form onSubmit={handleSubmit} className="mx-auto mt-12 max-w-[720px] space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Your name"
              required
              className="font-mono-tech border border-[var(--amber)]/40 bg-transparent px-5 py-4 text-[13px] tracking-[0.02em] text-[var(--foreground)] outline-none transition-colors placeholder:text-[var(--dim)] focus:border-[var(--amber)]"
            />
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Your email"
              required
              className="font-mono-tech border border-[var(--amber)]/40 bg-transparent px-5 py-4 text-[13px] tracking-[0.02em] text-[var(--foreground)] outline-none transition-colors placeholder:text-[var(--dim)] focus:border-[var(--amber)]"
            />
          </div>

          <textarea
            name="message"
            value={form.message}
            onChange={handleChange}
            placeholder="What's the vision? (Keep it high-signal)"
            required
            rows={6}
            className="font-mono-tech min-h-[160px] w-full resize-none border border-[var(--amber)]/40 bg-transparent px-5 py-4 text-[13px] tracking-[0.02em] text-[var(--foreground)] outline-none transition-colors placeholder:text-[var(--dim)] focus:border-[var(--amber)]"
          />

          <div className="flex flex-col items-center gap-4 md:flex-row md:justify-between">
            <p className="font-mono-tech text-[10px] tracking-[0.18em] text-[var(--dim)]">
              {form.message.length}/600
            </p>

            <button
              type="submit"
              disabled={status === "sending"}
              className="font-mono-tech bg-[var(--amber-bright)] px-7 py-4 text-[12px] font-bold uppercase tracking-[0.22em] text-[var(--background)] transition-colors hover:bg-bone disabled:opacity-60"
            >
              {buttonLabel}
            </button>
          </div>
        </form>

        {result && (
          <p
            className={`font-mono-tech mx-auto mt-5 text-[11px] tracking-[0.22em] ${
              status === "sent"
                ? "text-[var(--signal)]"
                : status === "error"
                  ? "text-[var(--blood)]"
                  : "text-[var(--dim)]"
            }`}
          >
            ▸ {result}
          </p>
        )}
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="hairline-t flex flex-col items-center justify-between gap-3 px-6 py-5 text-[9px] uppercase tracking-[0.18em] text-[var(--dim)] md:flex-row md:px-12">
      <div>OMNIFIC · BY PHIL · © 2026</div>
      <div className="flex gap-5">
        <a href="#audit" className="transition-colors hover:text-[var(--amber)]">
          Audit
        </a>
        <a href="#engage" className="transition-colors hover:text-[var(--amber)]">
          Engage
        </a>
      </div>
    </footer>
  );
}
